import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { isApiErrorEnvelope } from '@/api/form-errors'
import { useHelpRequests, useUpdateHelpRequest } from '@/help/api'
import { HELP_STATUS_VALUES, type HelpRequestStatus } from '@/help/schemas'
import { useDeleteMessage, useConversationMessages } from '@/messages/api'
import { useModeratePhoto, useUserPhotos, type PhotoModerationDecision } from '@/photos/api'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

function formatIsoDate(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('hu-HU')
  } catch {
    return '—'
  }
}

interface ErrorBanner {
  message: string
  traceId?: string
}

/**
 * `/help-requests` — admin „Segítségkérések" surface (DEC-SUPPORT-001 + DEC-MOD-007).
 *
 * The value over the retired /reports: each row shows the full page-context —
 * which page the user was on, the actual route, and the subject (peer becenév +
 * ULID, conversation ULID). From a row the moderator can open the subject's
 * photos / the conversation's messages and moderate them inline (DEC-MOD-007).
 */
export function HelpRequestList() {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState<HelpRequestStatus | undefined>(undefined)
  // DEC-ADMIN-015 kieg. — további szűrők + lapozó (a users-lista mintájára).
  const [pageFilter, setPageFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [nameFilter, setNameFilter] = useState('')
  const [nameQuery, setNameQuery] = useState('')
  const [page, setPage] = useState(1)
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null)
  // Which row has its photo / message moderation panel expanded (by row id).
  const [photosOpenFor, setPhotosOpenFor] = useState<number | null>(null)
  const [messagesOpenFor, setMessagesOpenFor] = useState<number | null>(null)
  const helpRequests = useHelpRequests({
    status: statusFilter,
    name: nameQuery || undefined,
    contextPage: pageFilter || undefined,
    category: categoryFilter || undefined,
    createdFrom: createdFrom || undefined,
    createdTo: createdTo || undefined,
    page,
  })
  const update = useUpdateHelpRequest()
  const meta = helpRequests.data?.meta
  const items = helpRequests.data?.items ?? []

  function resetPage() {
    setPage(1)
  }

  function onSearchSubmit(event: React.FormEvent) {
    event.preventDefault()
    setNameQuery(nameFilter.trim())
    resetPage()
  }

  function onFlip(id: number, target: HelpRequestStatus, confirmKey: string) {
    setErrorBanner(null)
    if (!window.confirm(t(confirmKey))) return
    update.mutate(
      { id, status: target },
      {
        onError: (error) => {
          if (isApiErrorEnvelope(error)) {
            setErrorBanner({ message: error.message, traceId: error.trace_id })
          } else {
            setErrorBanner({ message: t('helpRequests.flipError') })
          }
        },
      },
    )
  }

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="text-sm text-primary underline-offset-4 hover:underline self-start">
          {t('helpRequests.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('helpRequests.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('helpRequests.intro')}</p>
      </header>

      <nav className="flex flex-wrap gap-2" aria-label={t('helpRequests.statusLabel')}>
        <Button
          type="button"
          variant={statusFilter === undefined ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setStatusFilter(undefined)
            resetPage()
          }}
        >
          {t('helpRequests.filterAll')}
        </Button>
        {HELP_STATUS_VALUES.map((value) => (
          <Button
            key={value}
            type="button"
            variant={statusFilter === value ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setStatusFilter(value)
              resetPage()
            }}
          >
            {t(`helpRequests.status.${value}`)}
          </Button>
        ))}
      </nav>

      {/* DEC-ADMIN-015 kieg. — oldal / kategória / bejelentés-dátumtartomány. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('helpRequests.pageFilterLabel')}</span>
          <select
            className="rounded-md border bg-background px-2 py-1.5"
            value={pageFilter}
            onChange={(e) => {
              setPageFilter(e.target.value)
              resetPage()
            }}
          >
            <option value="">{t('helpRequests.filterAll')}</option>
            {['own_profile', 'public_profile', 'discovery', 'matches', 'messages', 'other'].map(
              (value) => (
                <option key={value} value={value}>
                  {t(`helpRequests.page.${value}`)}
                </option>
              ),
            )}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('helpRequests.categoryFilterLabel')}</span>
          <select
            className="rounded-md border bg-background px-2 py-1.5"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value)
              resetPage()
            }}
          >
            <option value="">{t('helpRequests.filterAll')}</option>
            {['content', 'app_issue', 'other'].map((value) => (
              <option key={value} value={value}>
                {t(`helpRequests.category.${value}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('helpRequests.createdFromLabel')}</span>
          <input
            type="date"
            className="rounded-md border bg-background px-2 py-1.5"
            value={createdFrom}
            onChange={(e) => {
              setCreatedFrom(e.target.value)
              resetPage()
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('helpRequests.createdToLabel')}</span>
          <input
            type="date"
            className="rounded-md border bg-background px-2 py-1.5"
            value={createdTo}
            onChange={(e) => {
              setCreatedTo(e.target.value)
              resetPage()
            }}
          />
        </label>
      </div>

      <form onSubmit={onSearchSubmit} className="flex gap-2">
        <Input
          type="search"
          value={nameFilter}
          onChange={(event) => setNameFilter(event.target.value)}
          placeholder={t('helpRequests.nameSearchPlaceholder')}
          aria-label={t('helpRequests.nameSearchLabel')}
        />
        <Button type="submit" variant="secondary">
          {t('users.search')}
        </Button>
      </form>

      {helpRequests.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}

      {helpRequests.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('helpRequests.loadError')}</AlertDescription>
        </Alert>
      )}

      {errorBanner && (
        <Alert variant="destructive">
          <AlertDescription>
            {errorBanner.message}
            {errorBanner.traceId && (
              <span className="block text-xs opacity-70">trace ID: {errorBanner.traceId}</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {!helpRequests.isPending && !helpRequests.isError && items.length === 0 && (
        <p className="text-muted-foreground">{t('helpRequests.empty')}</p>
      )}

      {!helpRequests.isPending && !helpRequests.isError && items.length > 0 && (
        <ul className="flex flex-col gap-3">
          {items.map((row, index) => {
            const open = row.status === 'open' || row.status === 'in_progress'
            const canModeratePhotos = row.id != null && row.subject_user_id
            const canModerateMessages = row.id != null && row.subject_conversation_id
            return (
              <li
                key={row.id ?? `idx-${index}`}
                className="flex flex-col gap-2 rounded-lg border p-3"
              >
                <div className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span className="font-medium">#{row.id ?? '—'}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                    {row.context_page ? t(`helpRequests.page.${row.context_page}`) : '—'}
                  </span>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium">
                    {row.status ? t(`helpRequests.status.${row.status}`) : '—'}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatIsoDate(row.created_at)}
                  </span>
                </div>

                {/* Page-context — the heart of the feature */}
                <dl className="flex flex-col gap-1 text-xs text-muted-foreground">
                  <div className="flex gap-2">
                    <dt className="font-medium">{t('helpRequests.routeLabel')}:</dt>
                    <dd className="font-mono break-all">{row.context_route ?? '—'}</dd>
                  </div>
                  {/* DEC-ADMIN-015 kieg. — a bejelentő + érintett a részletlapra
                      linkel (ott az e-mail = a kontakt-út). */}
                  {row.requester_user_id && (
                    <div className="flex gap-2">
                      <dt className="font-medium">{t('helpRequests.requesterLabel')}:</dt>
                      <dd className="break-all">
                        <Link
                          to={`/users/${row.requester_user_id}`}
                          className="text-primary underline underline-offset-4"
                        >
                          {row.requester_nick_name ?? '—'} ({row.requester_user_id})
                        </Link>
                      </dd>
                    </div>
                  )}
                  {row.subject_user_id && (
                    <div className="flex gap-2">
                      <dt className="font-medium">{t('helpRequests.subjectUserLabel')}:</dt>
                      <dd className="break-all">
                        <Link
                          to={`/users/${row.subject_user_id}`}
                          className="text-primary underline underline-offset-4"
                        >
                          {row.subject_nick_name ?? '—'} ({row.subject_user_id})
                        </Link>
                      </dd>
                    </div>
                  )}
                  {row.subject_conversation_id && (
                    <div className="flex gap-2">
                      <dt className="font-medium">{t('helpRequests.subjectConversationLabel')}:</dt>
                      <dd className="font-mono break-all">{row.subject_conversation_id}</dd>
                    </div>
                  )}
                  {row.category && (
                    <div className="flex gap-2">
                      <dt className="font-medium">{t('helpRequests.categoryLabel')}:</dt>
                      <dd>{t(`helpRequests.category.${row.category}`)}</dd>
                    </div>
                  )}
                  {row.handled_at && (
                    <div className="flex gap-2">
                      <dt className="font-medium">{t('helpRequests.handledAtLabel')}:</dt>
                      <dd>{formatIsoDate(row.handled_at)}</dd>
                    </div>
                  )}
                </dl>

                <p className="whitespace-pre-wrap text-sm">
                  <span className="font-medium">{t('helpRequests.messageLabel')}:</span>{' '}
                  {row.message ?? '—'}
                </p>

                {/* DEC-MOD-007 — moderáció a help-request kontextusából */}
                {(canModeratePhotos || canModerateMessages) && (
                  <div className="mt-1 flex flex-col gap-2 border-t pt-2">
                    <div className="flex flex-wrap gap-2">
                      {canModeratePhotos && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setPhotosOpenFor(photosOpenFor === row.id ? null : (row.id ?? null))
                          }
                        >
                          {photosOpenFor === row.id
                            ? t('helpRequests.moderateHide')
                            : t('helpRequests.moderatePhotos')}
                        </Button>
                      )}
                      {canModerateMessages && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setMessagesOpenFor(messagesOpenFor === row.id ? null : (row.id ?? null))
                          }
                        >
                          {messagesOpenFor === row.id
                            ? t('helpRequests.moderateHide')
                            : t('helpRequests.moderateMessages')}
                        </Button>
                      )}
                    </div>
                    {canModeratePhotos && photosOpenFor === row.id && row.subject_user_id && (
                      <PhotoModerationPanel userUlid={row.subject_user_id} />
                    )}
                    {canModerateMessages &&
                      messagesOpenFor === row.id &&
                      row.subject_conversation_id && (
                        <MessageModerationPanel conversationUlid={row.subject_conversation_id} />
                      )}
                  </div>
                )}

                {row.id != null && open && (
                  <div className="mt-1 flex flex-wrap gap-2 border-t pt-2">
                    {row.status === 'open' && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          row.id != null &&
                          onFlip(row.id, 'in_progress', 'helpRequests.flipInProgressConfirm')
                        }
                        disabled={update.isPending}
                      >
                        {t('helpRequests.flipInProgress')}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={() =>
                        row.id != null &&
                        onFlip(row.id, 'resolved', 'helpRequests.flipResolveConfirm')
                      }
                      disabled={update.isPending}
                    >
                      {t('helpRequests.flipResolve')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        row.id != null &&
                        onFlip(row.id, 'dismissed', 'helpRequests.flipDismissConfirm')
                      }
                      disabled={update.isPending}
                    >
                      {t('helpRequests.flipDismiss')}
                    </Button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* Lapozó (DEC-ADMIN-015 kieg.) — a backend eddig is lapozott, most látszik is. */}
      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-between gap-2 text-sm">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={helpRequests.isPending || meta.current_page <= 1}
          >
            {t('users.prevPage')}
          </Button>
          <span className="text-muted-foreground">
            {t('users.pageStatus', {
              current: meta.current_page,
              last: meta.last_page,
              total: meta.total,
            })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={helpRequests.isPending || meta.current_page >= meta.last_page}
          >
            {t('users.nextPage')}
          </Button>
        </div>
      )}
    </main>
  )
}

/**
 * Inline fotó-moderáló panel (DEC-MOD-007). A subject felhasználó fotóit listázza
 * (admin-nézet: ULID + státusz + utolsó döntés — a kép-URL-t a backend szándékosan
 * nem adja ki) + jóváhagyás / elutasítás / vizsgálat-alá gombokkal.
 */
function PhotoModerationPanel({ userUlid }: { userUlid: string }) {
  const { t } = useTranslation()
  const photos = useUserPhotos(userUlid)
  const moderate = useModeratePhoto()
  const [error, setError] = useState<string | null>(null)

  function onModerate(photoId: string, decision: PhotoModerationDecision, confirmKey: string) {
    setError(null)
    if (!window.confirm(t(confirmKey))) return
    moderate.mutate(
      { id: photoId, decision },
      {
        onError: (e) =>
          setError(isApiErrorEnvelope(e) ? e.message : t('helpRequests.photoModerateError')),
      },
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-md bg-muted/40 p-2">
      {photos.isPending && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}
      {photos.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('helpRequests.photosError')}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!photos.isPending && !photos.isError && (photos.data?.length ?? 0) === 0 && (
        <p className="text-xs text-muted-foreground">{t('helpRequests.photosEmpty')}</p>
      )}
      {(photos.data ?? []).map((p, i) => (
        <div
          key={p.id ?? `photo-${i}`}
          className="flex flex-col gap-2 border-b pb-2 text-xs last:border-b-0"
        >
          {(p.thumbnail_url || p.url) && (
            <img
              src={p.thumbnail_url ?? p.url ?? undefined}
              alt={p.id ?? 'fotó'}
              loading="lazy"
              className="max-h-40 w-auto self-start rounded-md border object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono break-all">{p.id}</span>
            <span className="rounded-full bg-secondary px-2 py-0.5">{p.status}</span>
            {p.latest_decision && (
              <span className="text-muted-foreground">({p.latest_decision})</span>
            )}
            {p.id && (
              <div className="ml-auto flex gap-1">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  disabled={moderate.isPending}
                  onClick={() => onModerate(p.id!, 'approved', 'helpRequests.photoApproveConfirm')}
                >
                  {t('helpRequests.photoApprove')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={moderate.isPending}
                  onClick={() => onModerate(p.id!, 'rejected', 'helpRequests.photoRejectConfirm')}
                >
                  {t('helpRequests.photoReject')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={moderate.isPending}
                  onClick={() =>
                    onModerate(p.id!, 'needs_review', 'helpRequests.photoNeedsReviewConfirm')
                  }
                >
                  {t('helpRequests.photoNeedsReview')}
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Inline üzenet-moderáló panel (DEC-MOD-007). A beszélgetés üzeneteit listázza
 * (szöveg + feladó ULID + idő; a már törölt üzenetek jelölve) + soft-delete gomb.
 */
function MessageModerationPanel({ conversationUlid }: { conversationUlid: string }) {
  const { t } = useTranslation()
  const messages = useConversationMessages(conversationUlid)
  const del = useDeleteMessage()
  const [error, setError] = useState<string | null>(null)

  function onDelete(messageId: string) {
    setError(null)
    if (!window.confirm(t('helpRequests.messageDeleteConfirm'))) return
    del.mutate(
      { id: messageId },
      {
        onError: (e) =>
          setError(isApiErrorEnvelope(e) ? e.message : t('helpRequests.messageDeleteError')),
      },
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-md bg-muted/40 p-2">
      {messages.isPending && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}
      {messages.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('helpRequests.messagesError')}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!messages.isPending && !messages.isError && (messages.data?.length ?? 0) === 0 && (
        <p className="text-xs text-muted-foreground">{t('helpRequests.messagesEmpty')}</p>
      )}
      {(messages.data ?? []).map((m, i) => (
        <div
          key={m.id ?? `msg-${i}`}
          className="flex flex-col gap-1 border-b pb-2 text-xs last:border-b-0"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono break-all text-muted-foreground">{m.sender_id}</span>
            <span className="text-muted-foreground">{formatIsoDate(m.created_at)}</span>
            {m.deleted_at && (
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-destructive">
                {t('helpRequests.messageDeletedBadge')}
              </span>
            )}
            {m.id && !m.deleted_at && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="ml-auto"
                disabled={del.isPending}
                onClick={() => onDelete(m.id!)}
              >
                {t('helpRequests.messageDelete')}
              </Button>
            )}
          </div>
          <p className="whitespace-pre-wrap break-words">{m.body}</p>
        </div>
      ))}
    </div>
  )
}
